/**
 * The trainer.
 *
 * One decision at a time: here is your seat, here are your cards, shove or fold. Then
 * the solver's answer and what the choice cost.
 *
 * Two rules shape the whole screen.
 *
 * A combination we cannot solve exactly is DISABLED, never quietly replaced by a
 * near-enough chart. That is the standing guardrail, and the selector below reads the
 * registry to enforce it rather than assuming what exists.
 *
 * And the verdict comes from the server. The client posts what it did; the API reads
 * the chart and says how it went. Grading here would be faster and would mean nothing.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useAuth } from "@clerk/clerk-react";
import { Link } from "react-router-dom";
import { api, ApiError, type ChartIndex, type ChartMeta, type ChartNode,
         type CoverageRow, type Grade, type Usage } from "../lib/api";
import { dealCards, seatPhrase, weightedHand, type DealtCard } from "../lib/cards";
import { SpotTable } from "../table/SpotTable";
import { RangeGrid } from "../components/RangeGrid";
import { DrillList } from "../components/DrillList";
import { HandHistory, HISTORY_MAX, MixBars, NodeReadout, Tile, WhyPanel,
         type HistoryRow } from "../components/TrainerPanels";
import { Card, Notice, Skeleton, UsageMeter } from "../components/ui";
import { actionForKey, shortcutFor } from "../lib/keys";
import { useCompact } from "../lib/compact";
import { DRILLS } from "../lib/drills";
import { Purity } from "../lib/purity";
import { contextLabel } from "../lib/labels";
import { useMeContext } from "../lib/MeContext";

interface Spot { node: ChartNode; meta: ChartMeta; index: ChartIndex; receipt: string }
interface Dealt { code: string; cards: DealtCard[] }
interface Answered { grade: Grade; action: string; code: string }

type GameType = "regular" | "knockout";

/**
 * One definition of what counts as a knockout, mirroring `isBountyChart` on the
 * server. `bounty_mode` has three ways of saying no and only one of them is falsy: the
 * chart files carry null, the registry key spells it "off". A plain truthiness check
 * here would put every regular chart behind the Pro knockout button.
 */
const NOT_KNOCKOUT = new Set(["", "off", "none"]);
const isKnockout = (mode: string | null): boolean =>
  mode !== null && !NOT_KNOCKOUT.has(mode.trim().toLowerCase());

/**
 * The caption sits under the name, so it must not repeat it. These read
 * "Progressive / Progressive — half paid..." when the label is included.
 */
const STRUCTURE_LABEL: Record<string, string> = {
  static: "Whole bounty, paid at once",
  progressive: "Half paid, half added to yours",
};

interface Session {
  hands: number;
  scored: number;
  correct: number;
  lossSum: number;
  /**
   * Consecutive correct decisions, and the best run of them this sitting.
   *
   * An indifferent hand does not break a streak and does not extend one — it is not a
   * decision that can be got wrong, so counting it either way would be a number about
   * the deal rather than about the player. Same rule accuracy already follows.
   */
  streak: number;
  best: number;
}

const EMPTY: Session = { hands: 0, scored: 0, correct: 0, lossSum: 0, streak: 0, best: 0 };

/** The registry, grouped so the selector can only offer real combinations. */
function useCoverage() {
  const { getToken } = useAuth();
  const [rows, setRows] = useState<CoverageRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  /**
   * `getToken` is a fresh closure on every render, so depending on it here fired the
   * effect on every render — and because the effect sets state, that is a fetch loop
   * that never settles. Held in a ref, with the effect depending on nothing. Same
   * remedy as Play.tsx and useMe.ts, for the same reason.
   */
  const tokenRef = useRef(getToken);
  tokenRef.current = getToken;
  useEffect(() => {
    let alive = true;
    api.coverage(tokenRef.current)
      .then((r) => { if (alive) setRows(r.coverage.filter((x) => x.status === "live")); })
      .catch((e) => { if (alive) setError(e instanceof ApiError ? e.humane : "Could not load the chart list."); });
    return () => { alive = false; };
  }, []);
  return { rows, error };
}

/**
 * PHASE 2B: what a Train CARD hands this page.
 *
 * The room's Train tab is a front door, not a second trainer — every card is one point
 * in the selection this page already has, and this is that point. It is a STARTING
 * POSITION and not a lock: the effects below still correct anything the published
 * library does not cover, which is the existing rule and the honest one. A card cannot
 * conjure a chart that has not been solved.
 */
export interface TrainPreset {
  gameType?: GameType;
  tableSize?: number;
  /** The ICM situation, matched against whatever payout presets are published. */
  icm?: string | null;
  depth?: number;
}

export function Train({ preset }: { preset?: TrainPreset } = {}) {
  const { getToken } = useAuth();
  const { me, refresh } = useMeContext();
  const { rows, error: coverageError } = useCoverage();

  /**
   * GAME TYPE IS A PRODUCT CHOICE, not another setting.
   *
   * Everything on this page used to be a dropdown of equal weight — table size beside
   * situation beside stack depth. Adding knockout as one more option inside one more
   * dropdown would have buried the single distinction a player most needs to see: a
   * knockout chart is a different game with different ranges, and it is priced
   * differently. Buried choices are how somebody trains the wrong product for a week.
   */
  const [gameType, setGameType] = useState<GameType>(preset?.gameType ?? "regular");
  const [structure, setStructure] = useState<string | null>(null);
  const [tableSize, setTableSize] = useState<number | null>(preset?.tableSize ?? null);
  const [contextKey, setContextKey] = useState<string | null>(null);
  const [depth, setDepth] = useState<number | null>(preset?.depth ?? null);

  const [spot, setSpot] = useState<Spot | null>(null);
  const [dealt, setDealt] = useState<Dealt | null>(null);
  const [answered, setAnswered] = useState<Answered | null>(null);
  const [busy, setBusy] = useState(false);
  /**
   * `busy` is state, so it is only safe against clicks React batches. Three
   * synchronous dispatched clicks posted three decisions and bought four spots. A ref
   * flips immediately, which is what a latch has to do.
   */
  const inFlight = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [session, setSession] = useState<Session>(EMPTY);
  const [history, setHistory] = useState<HistoryRow[]>([]);
  /**
   * Which drill. Only one of them is real, and the list says so on its own face — see
   * `DrillList`. It is state rather than a constant because the disabled entries have
   * to be able to look like siblings of a selected one, which they cannot do if there
   * is nothing to select.
   */
  const [drill, setDrill] = useState("preflop");

  /**
   * On a phone the setup column folds into one bar.
   *
   * You choose a table, a situation and a stack once and then drill twenty hands, and
   * the screen a hand is played on should not spend a screen and a half on the control
   * you touched at the start. On anything wider it is not a disclosure at all — it is
   * the left rail, always open, with no summary — which is why the component has to
   * know the width rather than leaving it all to the stylesheet.
   */
  const compact = useCompact();
  /**
   * OPEN IS STATE, and the prop is a pure function of it.
   *
   * It used to be `open={!compact || !spot || setupOpen}`. React only writes `open`
   * when the prop VALUE changes between renders, so collapsing the bar while that
   * expression still evaluated to `true` — which is exactly the case with no spot
   * loaded — left the DOM shut and React believing it was open. The bar stayed
   * collapsed, and with it the only button that starts the product.
   *
   * It starts open, because on first load there is nothing to fold around, and it
   * folds itself once a spot has been bought.
   */
  const [setupOpen, setSetupOpen] = useState(true);

  // One id for the whole sitting, so a run of hands can be read back as one run.
  const sessionId = useRef(`t_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`);

  /* ---------------------------------------------------------- selection --- */

  /**
   * Every selector below reads THIS, not `rows`, so a knockout chart can never be
   * reached from the regular side or the other way round. Filtering once, here, is
   * what stops the three dependent selectors from disagreeing about which product is
   * being configured.
   */
  const available = useMemo(
    () => (rows ?? []).filter((r) => (isKnockout(r.bounty_mode) ? "knockout" : "regular") === gameType),
    [rows, gameType]);

  /** Knockout structures that are actually published, for the second row of buttons. */
  const structures = useMemo(
    () => [...new Set(available.map((r) => r.bounty_mode).filter((m): m is string => !!m))].sort(),
    [available]);

  /** Narrowed further once a structure is chosen; identical to `pool` when regular. */
  const structured = useMemo(
    () => (gameType === "knockout" && structure ? available.filter((r) => r.bounty_mode === structure) : []),
    [available, gameType, structure]);

  const knockoutLive = useMemo(
    () => (rows ?? []).some((r) => isKnockout(r.bounty_mode)), [rows]);

  const tableSizes = useMemo(
    () => [...new Set(structured.length ? structured : available)]
      .map((r) => r.table_size)
      .filter((v, i, a) => a.indexOf(v) === i).sort((a, b) => a - b),
    [available, structured]);

  const contexts = useMemo(() => {
    const seen = new Map<string, { icm: string | null; payout: string | null; label: string }>();
    for (const r of (structured.length ? structured : available)) {
      if (tableSize !== null && r.table_size !== tableSize) continue;
      const key = `${r.icm_context ?? "none"}|${r.payout_preset ?? "none"}`;
      if (!seen.has(key)) {
        seen.set(key, { icm: r.icm_context, payout: r.payout_preset, label: contextLabel(r) });
      }
    }
    return [...seen.entries()].map(([key, v]) => ({ key, ...v }));
  }, [available, structured, tableSize]);

  const depths = useMemo(() => {
    const ctx = contexts.find((c) => c.key === contextKey);
    return [...new Set((structured.length ? structured : available)
      .filter((r) => r.table_size === tableSize)
      .filter((r) => !ctx || ((r.icm_context ?? "none") === (ctx.icm ?? "none")
                            && (r.payout_preset ?? "none") === (ctx.payout ?? "none")))
      .map((r) => r.stack_depth))].sort((a, b) => a - b);
  }, [available, structured, tableSize, contexts, contextKey]);

  /**
   * Default to a table size this plan can actually use.
   *
   * Picking the first live size landed a free account on "Heads-up — Pro", which is
   * marked disabled but is still the SELECTED value — so the one size they are allowed
   * was not the one chosen for them, and the only button on the screen failed.
   */
  /**
   * Memoised, because `me?.plan.tableSizes ?? []` builds a NEW empty array on every
   * render while the profile is loading — and it is in the dep array below, so the
   * effect ran on every render. It happens to be idempotent, so nothing looped; it is
   * one condition away from the render-storm this file's other comments describe.
   */
  const allowed = useMemo(() => me?.plan.tableSizes ?? [], [me]);
  /**
   * `resetSelection` is declared below, with the rest of the handlers, and this effect
   * runs above it. Held in a ref rather than moved: the declaration order down there
   * is the reading order — selection, dealing, acting — and it is worth more than
   * saving one indirection here.
   */
  const resetRef = useRef<() => void>(() => {});
  useEffect(() => {
    if (!tableSizes.length) return;
    const usable = allowed.length ? tableSizes.filter((n) => allowed.includes(n)) : tableSizes;
    const pool = usable.length ? usable : tableSizes;
    if (tableSize === null) { setTableSize(pool[0]!); return; }
    if (!pool.includes(tableSize)) {
      /**
       * MOVING A CHOSEN SIZE IS A NEW SITTING.
       *
       * Every path a person can take through the selects calls `resetSelection`, and
       * this effect was the one that did not. It fires when the pool changes under a
       * selection — a plan sync, an upgrade landing, a game type with different
       * coverage — and it used to move `tableSize` while leaving the spot and the
       * session in place. The next decision would then have been posted with the new
       * configuration's context beside the OLD spot's table size, and its accuracy
       * pooled with hands from a different game. Which is exactly what the comment on
       * `resetSelection` says must never happen.
       */
      setTableSize(pool[0]!);
      resetRef.current();
    }
  }, [tableSizes, tableSize, allowed]);
  useEffect(() => {
    if (contexts.length && !contexts.some((c) => c.key === contextKey)) setContextKey(contexts[0]!.key);
  }, [contexts, contextKey]);
  /**
   * A card's SITUATION, resolved once the coverage is known.
   *
   * A Train card names an ICM situation ("bubble") and never a payout preset, because
   * which presets are published is a property of the library on the day and a card that
   * named one would break the week the library changed. So the situation is matched
   * here against the contexts that actually exist, once, on the first render that has
   * any — and if there is no match the effect above has already chosen the first, which
   * is the trainer's own rule for an unavailable selection.
   */
  const presetPending = useRef(preset?.icm !== undefined);
  useEffect(() => {
    if (!presetPending.current || !contexts.length) return;
    presetPending.current = false;
    const want = preset?.icm ?? null;
    const hit = contexts.find((c) => (c.icm ?? null) === (want === "none" ? null : want));
    if (hit) setContextKey(hit.key);
  }, [contexts, preset]);
  /**
   * A knockout selection is incomplete without a structure, and the two structures are
   * different games rather than two settings of one — so one is chosen rather than
   * left null, and it is cleared entirely on the regular side so nothing downstream
   * can filter on a stale value.
   */
  useEffect(() => {
    if (gameType !== "knockout") { if (structure !== null) setStructure(null); return; }
    if (structures.length && (structure === null || !structures.includes(structure))) {
      setStructure(structures[0]!);
    }
  }, [gameType, structures, structure]);
  useEffect(() => {
    if (depths.length && (depth === null || !depths.includes(depth))) setDepth(depths[0]!);
  }, [depths, depth]);

  const selection = useMemo(() => {
    const ctx = contexts.find((c) => c.key === contextKey);
    if (tableSize === null || depth === null || !ctx) return null;
    return { format: "mtt", variant: "standard", tableSize, stackDepth: depth,
             icmContext: ctx.icm, payoutPreset: ctx.payout,
             // Hardcoded null until now, which is why no knockout chart was reachable
             // even once one existed: the key would always have resolved to "off".
             bountyMode: gameType === "knockout" ? structure : null };
  }, [tableSize, depth, contexts, contextKey, gameType, structure]);

  /* ------------------------------------------------------------- dealing --- */

  const deal = useCallback((node: ChartNode) => {
    const code = weightedHand(node.strategy, Math.random);
    setDealt({ code, cards: dealCards(code, Math.random) });
    setAnswered(null);
    // A failed grade left its red notice sitting above every subsequent hand.
    setError(null);
  }, []);

  /**
   * A session is one set of conditions. Accuracy pooled across a nine-handed bubble
   * and a heads-up chip-EV spot is a number about nothing, so changing the selection
   * starts a new one.
   */
  const resetSelection = useCallback(() => {
    setSpot(null); setDealt(null); setAnswered(null);
    setSession(EMPTY); setHistory([]); setError(null);
    // Nothing to fold around again, and the way in has to be visible.
    setSetupOpen(true);
    sessionId.current = `t_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
  }, []);
  resetRef.current = resetSelection;

  /**
   * A new spot costs a spot from the daily allowance: it is a fresh decision fetched
   * from the library. Dealing another hand at the SAME spot is free, which is both
   * honest metering and the way people actually drill — you learn a position by
   * seeing many hands in it, not by seeing many positions once.
   */
  const newSpot = useCallback(async () => {
    if (!selection || inFlight.current) return;
    inFlight.current = true;
    setBusy(true); setError(null);
    try {
      const idx = await api.chartIndex(getToken, selection);
      /**
       * Every decision in the chart, not just the opens.
       *
       * This used to filter the caller nodes out, which threw away three quarters of
       * each chart and all of the harder half of the format — calling off is the
       * decision people actually lose money on, and the screen had copy written for it
       * that could never render.
       */
      const pick = idx.index.nodes[Math.floor(Math.random() * idx.index.nodes.length)];
      if (!pick) throw new Error("chart has no decisions");
      const got = await api.chartNode(getToken, selection, pick.path);
      /**
       * Deal FIRST. The spot is already paid for server-side by the time this
       * response arrives, so a throw between recording the usage and dealing left the
       * meter ticked up and the screen showing an error — charged for nothing, with no
       * way to tell that had happened.
       */
      if (!Object.keys(got.node.strategy ?? {}).length) throw new Error("empty strategy");
      setSpot({ node: got.node, meta: got.meta, index: idx.index, receipt: got.receipt });
      deal(got.node);
      // Phone only in effect: on a desktop the disclosure is held open regardless.
      setSetupOpen(false);
      setUsage(got.usage);
      void refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.humane : "Could not load that spot.");
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }, [selection, getToken, deal, refresh]);

  /* --------------------------------------------------------------- act --- */

  const act = useCallback(async (actionId: string) => {
    if (!spot || !dealt || answered || busy || inFlight.current) return;
    inFlight.current = true;
    setBusy(true); setError(null);
    try {
      const res = await api.grade(getToken, [{
        sessionId: sessionId.current,
        ts: Math.floor(Date.now() / 1000),
        format: "mtt",
        variant: "standard",
        drill: "shove_fold",
        tableSize: spot.index.tableSize,
        heroStackBb: spot.index.stackDepth,
        position: spot.node.actor,
        icmContext: selection?.icmContext ?? null,
        payoutPreset: selection?.payoutPreset ?? null,
        // The decision is recorded against the game it was made in, so the leak report
        // never mixes a knockout jam in with a regular one. They are different games.
        bountyMode: selection?.bountyMode ?? null,
        nodeId: `${spot.meta.key}#${spot.node.path}`,
        street: "preflop",
        hand: dealt.code,
        action: actionId,
        bountyEv: 0,
        source: "drill",
        // Proof this spot was served to this account. Without it the server returns
        // 409 spot_not_open rather than scoring.
        receipt: spot.receipt,
      }]);
      const grade = res.grades[0];
      if (!grade) throw new Error("no verdict returned");
      setAnswered({ grade, action: actionId, code: dealt.code });
      setSession((s) => {
        const streak = grade.indifferent ? s.streak : grade.correct ? s.streak + 1 : 0;
        return {
          hands: s.hands + 1,
          scored: s.scored + (grade.indifferent ? 0 : 1),
          correct: s.correct + (grade.indifferent ? 0 : grade.correct ? 1 : 0),
          lossSum: s.lossSum + (grade.indifferent ? 0 : grade.evLostNormalized),
          streak,
          best: Math.max(s.best, streak),
        };
      });
      /**
       * Newest first, and BOUNDED. An unbounded list is a memory leak with a scrollbar:
       * somebody drilling for an hour is a few thousand rows of React nodes, all of
       * them mounted, for a panel whose whole job is to show the last few.
       */
      const label = (id: string) => spot.node.actions.find((a) => a.id === id)?.label ?? id;
      setHistory((h) => [{
        code: dealt.code,
        action: actionId,
        actionLabel: label(actionId),
        bestLabel: grade.best ? label(grade.best) : null,
        cost: grade.indifferent ? 0 : grade.evLostNormalized,
        correct: grade.correct,
        indifferent: grade.indifferent,
      }, ...h].slice(0, HISTORY_MAX));
    } catch (e) {
      setError(e instanceof ApiError ? e.humane : "Could not grade that.");
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }, [spot, dealt, answered, busy, getToken, selection]);

  /* ----------------------------------------------------------- keyboard --- */

  /**
   * The whole loop from the keyboard.
   *
   * Held in refs rather than the dep array: rebinding a window listener on every
   * render is how a handler ends up attached twice and an action posted twice, and
   * this file has already paid for one double-post (see `inFlight`).
   */
  const kb = useRef({ spot, answered, busy, act, deal });
  kb.current = { spot, answered, busy, act, deal };
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      // Typing in a field is typing, not a shortcut. Buttons and links are fine,
      // because every key below is a letter or a digit — see the note on Enter.
      if (t && (t.isContentEditable
                || t.tagName === "INPUT" || t.tagName === "SELECT" || t.tagName === "TEXTAREA")) return;
      const { spot: sp, answered: an, busy: bz } = kb.current;
      if (!sp || bz) return;
      if (an) {
        /*
         * ======================== ENTER IS NOT A SHORTCUT ========================
         * It was, and it took the key away from every button and link on the page
         * for as long as a hand was graded — which is half of the time this screen
         * is on. `preventDefault` on a keydown cancels a button's and a link's
         * activation, so tabbing to "New spot" and pressing Enter re-dealt the old
         * node instead of buying a spot, and tabbing to a nav link and pressing
         * Enter dealt a hand instead of navigating. Space still worked, which is
         * worse than either behaviour on its own.
         *
         * It was also a way to shove a hand nobody saw. Enter dealt, `ActionBar`
         * focused its first button — the jam — and a second Enter (or one held key
         * repeating) fell through to that button's native activation and posted it.
         * Graded, counted, and in the streak.
         *
         * N deals the next hand. Enter does what Enter does.
         * ========================================================================
         */
        if (e.key.toUpperCase() === "N") { e.preventDefault(); kb.current.deal(sp.node); }
        return;
      }
      const id = actionForKey(e.key, sp.node.actions);
      if (id) { e.preventDefault(); void kb.current.act(id); }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /* -------------------------------------------------------------- render --- */

  if (coverageError) return <Card><Notice kind="err">{coverageError}</Notice></Card>;
  if (!rows) return <Card><Skeleton lines={5} /></Card>;
  if (rows.length === 0) {
    return (
      <Card>
        <h1 className="display">Trainer</h1>
        <Notice kind="info">
          No charts are published yet. They are solved and waiting — nothing will be shown here
          until the library is live.
        </Notice>
      </Card>
    );
  }

  const node = spot?.node ?? null;
  const strategy = node && dealt ? node.strategy[dealt.code] : undefined;

  /**
   * The setup bar's own label, when it is folded.
   *
   * Every part of it is the CURRENT selection rather than a fixed caption, because the
   * whole justification for hiding four controls behind one bar is that the bar still
   * says what they are set to. A bar reading "Setup" would just be a door.
   */
  const setupLine = [
    DRILLS.find((d) => d.id === drill)?.label ?? "Preflop",
    tableSize === null ? null : tableSize === 2 ? "Heads-up" : `${tableSize}-handed`,
    contexts.find((c) => c.key === contextKey)?.label ?? null,
    depth === null ? null : `${depth} bb`,
  ].filter(Boolean).join("  ·  ");

  return (
    <div className="trainer">
      <header className="trainer-head">
        <div>
          <p className="eyebrow">Shove or fold</p>
          <h1 className="display">Trainer</h1>
        </div>
        {/* From the last response if we have one, otherwise from the account — so the
            allowance is visible BEFORE spending from it, not only after. */}
        {(usage ?? me?.usage) && (
          <div className="trainer-usage">
            <UsageMeter used={(usage ?? me!.usage).used} limit={(usage ?? me!.usage).limit} />
          </div>
        )}
      </header>

      {error && <Notice kind="err">{error}</Notice>}

      <div className="trainer-grid">
        {/* ------------------------------------------------------------ left --- */}
        <aside className="tr-col tr-left">
          {/*
            * `.tr-left` is a rail on a desktop and NOTHING on a phone — the media
            * query gives it `display: contents`, so these three blocks become grid
            * items in their own right and can be ordered around the felt. That is
            * what lets the setup sit above the hand and the session numbers below the
            * answer without the markup differing between the two.
            */}
          {/* On anything wider than a phone it is not a disclosure — it is the rail. */}
          <details className="tr-setup" open={compact ? setupOpen : true}
                   onToggle={(e) => setSetupOpen(e.currentTarget.open)}>
            <summary>
              <span className="tr-setup-line">{setupLine}</span>
            </summary>
            <div className="tr-col tr-setup-body">
          <Panel title="Drill">
            <DrillList value={drill} onChange={setDrill} />
          </Panel>

          <Panel title="Setup">
            <GamePicker
              value={gameType} onChange={(g) => { setGameType(g); resetSelection(); }}
              knockoutLive={knockoutLive}
              knockoutAllowed={me?.plan.capabilities?.includes("bounty_drills") ?? false}
              structures={structures} structure={structure}
              onStructure={(v) => { setStructure(v); resetSelection(); }}
              disabled={busy}
            />

            <div className="picker picker-stacked">
              <label>
                <span>Table</span>
                <select value={tableSize ?? ""} disabled={busy}
                        onChange={(e) => { setTableSize(Number(e.target.value)); resetSelection(); }}>
                  {tableSizes.map((n) => (
                    <option key={n} value={n} disabled={allowed.length > 0 && !allowed.includes(n)}>
                      {n === 2 ? "Heads-up" : `${n}-handed`}
                      {allowed.length > 0 && !allowed.includes(n) ? " — Pro" : ""}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                <span>Situation</span>
                <select value={contextKey ?? ""} disabled={busy}
                        onChange={(e) => { setContextKey(e.target.value); resetSelection(); }}>
                  {contexts.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
              </label>

              <label>
                <span>Stack</span>
                <select value={depth ?? ""} disabled={busy}
                        onChange={(e) => { setDepth(Number(e.target.value)); resetSelection(); }}>
                  {depths.map((d) => <option key={d} value={d}>{d} bb</option>)}
                </select>
              </label>

              <button className="btn btn-block" onClick={newSpot} disabled={busy || !selection}>
                {spot ? "New spot" : "Start"}
              </button>
            </div>
          </Panel>
            </div>
          </details>

          {/*
            * The session numbers and the hands they came from, together.
            *
            * The history started in the right rail and made it a column twice the
            * height of the other two. It also belonged here: "82% accuracy" and "the
            * four you missed were offsuit aces" are one thought, and reading them off
            * opposite edges of a 1400px screen is not how anybody holds a thought.
            */}
          <Panel title="This session">
            <SessionTiles session={session} />
          </Panel>

          <Panel title="Last hands">
            <HandHistory rows={history} />
          </Panel>
        </aside>

        {/* ---------------------------------------------------------- centre --- */}
        <section className="tr-col tr-mid">
          {!spot || !dealt || !node ? (
            <div className="tr-empty">
              <p className="muted">
                Pick a table, a situation and a stack, then press Start. Every spot is a
                decision from a published solve — nothing here is generated to fill the screen.
              </p>
            </div>
          ) : (
            <>
              {/*
                * `chip-dup` marks the three the SETUP BAR already carries — table,
                * stack and street. On a desktop the bar is a rail and the chips are
                * the only place those appear beside the felt, so both are shown. On a
                * phone the bar is two inches above them, and three chips repeating it
                * cost a whole row of a screen that has none to spare.
                */}
              <div className="chips" role="group" aria-label="This spot">
                <span className="chip chip-dup">{spot.index.tableSize === 2 ? "Heads-up" : `${spot.index.tableSize}-handed`}</span>
                <span className="chip chip-dup">{spot.index.stackDepth} bb</span>
                <span className="chip chip-dup">Preflop</span>
                <span className="chip chip-hero">You are {seatPhrase(node.actor)}</span>
                <span className="chip">Pot <span className="num">{node.potBb.toFixed(2)}</span> bb</span>
                <span className={`chip ${answered ? "chip-done" : "chip-live"}`}>
                  {answered ? "Graded" : "Your turn"}
                </span>
              </div>

              <SpotTable node={node} cards={dealt.cards} handCode={dealt.code} />
              <p className="hand-line">
                You hold <span className="num hand-code-strong">{dealt.code}</span>
              </p>

              {!answered ? (
                <ActionBar node={node} busy={busy} onAct={act} dealtCode={dealt.code} />
              ) : (
                <Verdict node={node} dealt={dealt} answered={answered} strategy={strategy}
                         busy={busy} onNext={() => deal(node)} />
              )}

              <KeyLegend actions={node.actions} answered={answered !== null} />

              {/*
                * Provenance at the foot, not the headline.
                *
                * It used to open the spot, above the felt, where it was the first thing
                * on the screen every hand — an important sentence read once and skipped
                * four hundred times. It belongs where somebody goes looking for it.
                */}
              <div className="spot-prov"><Precision meta={spot.meta} /></div>
            </>
          )}
        </section>

        {/* ----------------------------------------------------------- right --- */}
        <aside className="tr-col tr-right">
          {!spot || !node ? (
            <Panel title="Node">
              <p className="muted small">
                Nothing is loaded. The panels here read the chart node itself, so they
                stay empty rather than showing a shape with no numbers in it.
              </p>
            </Panel>
          ) : (
            <>
              <Panel title="This node">
                <NodeReadout node={node} dealtCode={dealt?.code ?? null}
                             tableSize={spot.index.tableSize} stackDepth={spot.index.stackDepth}
                             revealed={answered !== null} />
              </Panel>

              <Panel title="Cost of the deviation">
                {answered ? (
                  <Deviation node={node} answered={answered} />
                ) : (
                  <p className="muted small">Play the hand and the cost appears here.</p>
                )}
              </Panel>

              <Panel title="Equilibrium mix">
                {answered && dealt ? (
                  <MixBars node={node} dealtCode={dealt.code} best={answered.grade.best} />
                ) : (
                  <p className="muted small">Covered until the hand is graded.</p>
                )}
              </Panel>

              <Panel title="Why">
                <WhyPanel node={node} dealtCode={dealt?.code ?? null}
                          grade={answered?.grade ?? null} revealed={answered !== null} />
              </Panel>

              <Panel title="The whole range">
                <RangeGrid node={node} dealtCode={dealt?.code ?? null} revealed={answered !== null} />
              </Panel>

            </>
          )}
        </aside>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ panel --- */

/** A titled block in a rail. The rails are not `Card`s: a card inside a card reads as a mistake. */
function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="tr-panel">
      <h2 className="tr-panel-h">{title}</h2>
      {children}
    </section>
  );
}

/* ------------------------------------------------------------- action bar --- */

function ActionBar({ node, busy, onAct, dealtCode }: {
  node: ChartNode; busy: boolean; onAct: (id: string) => void; dealtCode: string;
}) {
  /**
   * Keep the keyboard in the loop.
   *
   * Acting replaces the action buttons with the verdict and "Next hand" replaces them
   * back — both times the focused element is unmounted and focus falls to the body. A
   * keyboard user then had to tab through three selects and the New spot button to
   * reach the actions again, EVERY HAND. That is the entire product loop, unusable
   * without a mouse.
   */
  const first = useRef<HTMLButtonElement>(null);
  useEffect(() => { first.current?.focus(); }, [dealtCode]);

  return (
    <div className="acts">
      {node.actions.map((a, i) => {
        const key = shortcutFor(a.id);
        return (
          <button key={a.id} ref={i === 0 ? first : undefined}
                  className={`btn ${a.id === "fold" ? "btn-quiet" : ""}`}
                  disabled={busy} onClick={() => onAct(a.id)}>
            {key && <kbd className="btn-key">{key}</kbd>}
            {a.label}
            {/* A separate node, or JSX collapses the space and it reads "Call19.88". */}
            {a.size !== undefined && a.id !== "fold"
              ? <em>{" "}{a.size.toFixed(a.size % 1 === 0 ? 0 : 2)} bb</em> : null}
          </button>
        );
      })}
    </div>
  );
}

/* ---------------------------------------------------------------- legend --- */

/**
 * The keys, on screen.
 *
 * Built from the node's own actions, so it can never advertise a key for an action
 * that is not there. `N` is listed only once a hand has been graded, because that is
 * the only time it does anything.
 */
function KeyLegend({ actions, answered }: {
  actions: ChartNode["actions"]; answered: boolean;
}) {
  const keyed = actions
    .map((a) => ({ label: a.label, key: shortcutFor(a.id) }))
    .filter((x): x is { label: string; key: string } => x.key !== null);
  return (
    <p className="keylegend">
      {keyed.map((k) => (
        <span key={k.key}><kbd>{k.key}</kbd> {k.label}</span>
      ))}
      {answered && <span><kbd>N</kbd> Next hand</span>}
    </p>
  );
}

/* -------------------------------------------------------------- deviation --- */

/**
 * What the choice cost, in the unit the solve actually optimised.
 *
 * NOT converted to big blinds. `evLostNormalized` is a share of what was at stake at
 * this node, and the preflop pipeline optimises in prize units rather than chips — so
 * multiplying it by the pot would produce a bb figure that looks precise and means
 * nothing. The number shown is the number the server computed.
 */
function Deviation({ node, answered }: { node: ChartNode; answered: Answered }) {
  const g = answered.grade;
  const label = (id: string) => node.actions.find((a) => a.id === id)?.label ?? id;
  if (g.indifferent) {
    return (
      <p className="dev dev-even">
        <span className="dev-value num">0.00%</span>
        <span className="dev-note">Nothing. The two actions are worth the same here.</span>
      </p>
    );
  }
  if (g.correct) {
    return (
      <p className="dev dev-ok">
        <span className="dev-value num">0.00%</span>
        <span className="dev-note">{label(answered.action)} is the play.</span>
      </p>
    );
  }
  return (
    <p className="dev dev-bad">
      <span className="dev-value num">{(g.evLostNormalized * 100).toFixed(2)}%</span>
      {/*
        * The labels are never conjugated. "Fold" + "ing" happens to work and "All in"
        * + "ing" is not a word, so the sentence is built to take the label whole.
        */}
      <span className="dev-note">
        of what was at stake, given up by choosing {label(answered.action)} over{" "}
        {label(g.best ?? "")}.
      </span>
    </p>
  );
}

/* ------------------------------------------------------------ game picker --- */

/**
 * Which game you are training, as buttons rather than a fourth dropdown.
 *
 * ============================ WHY BUTTONS ======================================
 * This page presented everything as a select: table size beside situation beside
 * stack depth, all the same weight. Game type is not that kind of choice. A knockout
 * chart is a DIFFERENT GAME — the ranges move because a bounty pays you for calling —
 * and it is a different product, priced differently. Put as one more option inside one
 * more dropdown, it is the sort of thing somebody trains for a week without noticing
 * they picked it, or never finds at all.
 *
 * Three states have to be legible without a click, which is the other reason this is
 * not a select — a disabled <option> explains nothing:
 *
 *   AVAILABLE    the button works.
 *   NOT YET      no knockout chart has passed the gate, so there is nothing to serve.
 *                Said plainly rather than hidden: the standing rule for this product
 *                is that an unavailable combination is shown as unavailable.
 *   PRO          knockout is a paid capability. The button says so and points at
 *                pricing instead of failing on click.
 * ================================================================================
 */
function GamePicker({ value, onChange, knockoutLive, knockoutAllowed, structures, structure,
                      onStructure, disabled }: {
  value: GameType; onChange: (g: GameType) => void;
  knockoutLive: boolean; knockoutAllowed: boolean;
  structures: string[]; structure: string | null;
  onStructure: (v: string) => void; disabled: boolean;
}) {
  const blocked = !knockoutLive ? "not yet" : !knockoutAllowed ? "Pro" : null;
  return (
    <div className="gamepick">
      <div className="productpick-row" role="group" aria-label="Game type">
        <button type="button" className={`product-btn ${value === "regular" ? "product-on" : ""}`}
                aria-pressed={value === "regular"} disabled={disabled}
                onClick={() => onChange("regular")}>
          Tournament
          <em>Regular payouts</em>
        </button>
        {/*
          * A gated button that does nothing is a dead end, and a dead end reads as a
          * bug. When knockout is gated on the PLAN there is somewhere useful to go, so
          * it becomes a link to pricing rather than an inert control.
          *
          * "Not yet" is different and stays disabled: nothing is published, so there is
          * nothing to sell and nowhere to send anybody. Refusing to fake a destination
          * is the same rule the chart registry follows when it marks a combination
          * withheld instead of substituting a near one.
          */}
        {blocked === "Pro" ? (
          <Link to="/pricing" className="product-btn product-locked">
            Knockout
            <em>Bounty drills are part of Pro</em>
          </Link>
        ) : (
          <button type="button" className={`product-btn ${value === "knockout" ? "product-on" : ""}`}
                  aria-pressed={value === "knockout"} disabled={disabled || blocked !== null}
                  title={blocked ? "No knockout charts have been published yet" : undefined}
                  onClick={() => onChange("knockout")}>
            Knockout
            <em>{blocked ? "Not yet" : "Bounties in play"}</em>
          </button>
        )}
      </div>

      {value === "knockout" && structures.length > 0 && (
        <div className="productpick-row productpick-sub" role="group" aria-label="Bounty structure">
          {structures.map((m) => (
            <button key={m} type="button" className={`product-btn ${structure === m ? "product-on" : ""}`}
                    aria-pressed={structure === m} disabled={disabled}
                    onClick={() => onStructure(m)}>
              {m === "static" ? "Static" : m === "progressive" ? "Progressive" : m}
              <em>{STRUCTURE_LABEL[m] ?? "Bounty structure"}</em>
            </button>
          ))}
        </div>
      )}

      {value === "knockout" && (
        <p className="muted small">
          A bounty pays you for knocking somebody out, so every player calls wider than
          they would in a regular tournament. These ranges are not interchangeable with
          the ones on the other tab.
        </p>
      )}
    </div>
  );
}

/* -------------------------------------------------------------- precision --- */

/**
 * How wrong this chart is allowed to be, on the chart itself.
 *
 * ============================ WHY IT IS PER CHART ===============================
 * The alternative was a single advertised figure in the marketing copy. Every product
 * in this category does that, and it is how "GTO" came to mean nothing: one number,
 * chosen from the best case, applied to everything.
 *
 * It also would not have survived PKO. Bounty charts are held to a ceiling four times
 * looser than the rest of the library, for a measured reason — the solver models one
 * caller, a knockout bounty makes every seat behind call wider, and wider calls are
 * exactly the case being abstracted away. One library-wide number would either have
 * understated the bounty charts or defamed all the others.
 *
 * So the figure travels with the chart: measured by `verify/abstraction-error.ts` or
 * `verify/bounty-error.ts`, recorded by `scripts/publish-charts.ts` into the same
 * registry row the gate consulted, and rendered here. The number the app shows is the
 * number that decided whether the chart could be published at all.
 *
 * NULL IS NOT ZERO. A chart with no measurement says so rather than rendering 0.00%,
 * which would be the most flattering possible reading of "nobody checked". The same
 * rule the leak report follows for unmeasured decisions.
 * ================================================================================
 */
function Precision({ meta }: { meta: Spot["meta"] }) {
  const a = meta.abstractionError;
  if (a === null || a === undefined || !Number.isFinite(a)) {
    return (
      <span className="purity-row">
        <span className="muted small precision">Model fidelity not measured for this chart.</span>
      </span>
    );
  }
  /**
   * Two significant figures below a tenth of a percent, one above. "0.1%" reads as a
   * round claim; "0.09%" reads as a measurement, which is what it is.
   *
   * And below a hundredth, a BOUND rather than a rounded zero. The heads-up charts
   * measure about 0.003% — genuinely tiny, because with one seat behind the one-caller
   * model is not an approximation at all — which `toFixed(2)` renders as "0.00%". A
   * precision claim of exactly zero reads as a placeholder or a lie, and it is the one
   * number this line must never appear to make.
   */
  const pct = a * 100;
  const shown = pct < 0.01 ? "<0.01" : pct < 0.1 ? pct.toFixed(2) : pct.toFixed(1);
  return (
    <span className="purity-row">
      <Purity tier={meta.purity} measured={a} />
      <span className="muted small precision">
        Solved to <span className="num">{shown}%</span> of pot — the measured gap between
        the model and the game, not a target.
      </span>
    </span>
  );
}

/* ---------------------------------------------------------------- verdict --- */

function Verdict({ node, dealt, answered, strategy, busy, onNext }: {
  node: ChartNode; dealt: Dealt; answered: Answered;
  strategy: ChartNode["strategy"][string] | undefined; busy: boolean; onNext: () => void;
}) {
  /**
   * Grading unmounts the action buttons, so focus falls to the body. "Next hand" takes
   * it, which keeps the loop going from the keyboard — the same reason `ActionBar`
   * focuses its first button when a hand is dealt. The ref used to be passed down from
   * the parent; it belongs to the button it focuses.
   */
  const nextRef = useRef<HTMLButtonElement>(null);
  useEffect(() => { nextRef.current?.focus(); }, [answered.code]);

  const g = answered.grade;
  const label = (id: string) => node.actions.find((a) => a.id === id)?.label ?? id;

  /**
   * Three verdicts, not two. A hand can be genuinely indifferent — the two actions
   * are inside what the pipeline can resolve — and calling that "wrong" would be
   * marking someone down for a correct choice. It is also the most interesting thing
   * a trainer can tell you, so it gets its own state rather than a footnote.
   */
  const tone = g.indifferent ? "info" : g.correct ? "ok" : "err";
  const verdictId = "trainer-verdict";

  /**
   * ===================== WHY THERE IS NO TABLE HERE ANY MORE ====================
   * This used to print Action / Equilibrium / Cost underneath the notice. The right
   * rail now shows exactly those three quantities — the mix as bars, the cost as one
   * large number — and two renderings of one set of numbers, on screen at the same
   * time, is how they come to disagree. The rail won because bars are the better
   * reading of a mix, and because the centre column has to stay the same height from
   * hand to hand: a table that is two rows tall for a shove node and three for a
   * future one would move the felt every time it appeared.
   *
   * `strategy` is still taken as a prop and still decides whether there is anything
   * to say at all — a hand the chart does not cover is said to be uncovered, rather
   * than given a verdict about numbers nobody solved.
   * ============================================================================== */
  return (
    <div className="verdict" id={verdictId}>
      <Notice kind={tone}>
        {g.indifferent ? (
          <><strong>Either is fine.</strong> {dealt.code} sits inside what this solve can
          resolve — the two actions are worth the same, so neither is a mistake.</>
        ) : g.correct ? (
          <><strong>Correct.</strong> {label(g.best ?? "")} is the play with {dealt.code}.</>
        ) : (
          <><strong>{label(g.best ?? "")}.</strong> {label(answered.action)} costs{" "}
          <span className="num">{(g.evLostNormalized * 100).toFixed(2)}%</span> of what was
          at stake here.</>
        )}
      </Notice>

      {!strategy && (
        <p className="muted small">
          This chart carries no entry for {dealt.code}, so the panels beside this one are
          empty rather than guessing at one.
        </p>
      )}

      <div className="acts">
        {/*
          * Disabled while a request is in flight, like every other control. It was
          * not, so clicking it during a New spot round-trip dealt a hand at the old
          * node that was replaced the moment the response landed — a hand that
          * flashed up and was never really in play. The keyboard path was already
          * guarded; the mouse path was not.
          *
          * `aria-describedby` points at the verdict. A correct or indifferent result
          * is a polite live region, and focus moving here in the same commit wins the
          * race — a screen reader announced mistakes and stayed silent about correct
          * answers. Naming the verdict as this button's description means it is read
          * either way.
          */}
        <button ref={nextRef} className="btn" disabled={busy}
                aria-describedby={verdictId} onClick={onNext}>
          <kbd className="btn-key">N</kbd>Next hand
        </button>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- session --- */

/**
 * The sitting, as four tiles.
 *
 * Streak is the fourth because it is the one a person actually watches. Accuracy over
 * a hundred hands barely moves; a run of eleven correct decisions that ends is felt
 * immediately, and feeling something is what brings somebody back to the next hand.
 */
function SessionTiles({ session }: { session: Session }) {
  const acc = session.scored ? (session.correct / session.scored) * 100 : 0;
  const loss = session.scored ? (session.lossSum / session.scored) * 100 : 0;
  return (
    <>
      <ul className="tiles">
        <Tile label="Hands" value={String(session.hands)} />
        <Tile label="Accuracy" value={session.scored ? `${acc.toFixed(0)}%` : "\u2014"} />
        <Tile label="Average loss" value={session.scored ? `${loss.toFixed(2)}%` : "\u2014"} />
        <Tile label="Streak" value={String(session.streak)}
              hint={session.best > session.streak ? `best ${session.best}` : undefined} />
      </ul>
      {session.hands > session.scored && (
        <p className="muted small">
          {session.hands - session.scored} of these were indifferent and are left out of
          accuracy, average loss and the streak — a hand that cannot be played wrong says
          nothing about how you played.
        </p>
      )}
    </>
  );
}
